import type { BookingStatus, QuotationStatus, RequestStatus } from '@navicosa/shared-types';
/**
 * Kiểu dữ liệu Admin dùng – khớp phản hồi của apps/api (Sprint 2).
 * Ghi chú: OpenAPI hiện chưa mô tả body/response (Zod chưa xuất sang OpenAPI) nên kiểu viết tay ở đây;
 * sẽ thay bằng @navicosa/api-client sinh tự động khi bổ sung schema OpenAPI (Sprint 3).
 */
import type { PriceBasis, PricingMode, ProductStatus, RentalPeriod } from '@navicosa/shared-types';

export interface Me {
  id: string;
  email: string;
  full_name: string;
  roles: string[];
  permissions: string[];
}

export interface Category {
  id: string;
  parent_id: string | null;
  name: string;
  slug: string;
  description: string | null;
  sort_order: number;
  is_active: boolean;
}

export interface Brand {
  id: string;
  name: string;
  slug: string;
  is_active: boolean;
}

export interface Location {
  id: string;
  parent_id: string | null;
  name: string;
  code: string | null;
  location_type: string;
  is_active: boolean;
}

export interface SpecDefinition {
  id: string;
  category_id: string;
  spec_key: string;
  label: string;
  data_type: 'number' | 'text' | 'boolean' | 'option';
  unit: string | null;
  is_filterable: boolean;
  is_required: boolean;
  sort_order: number;
  options: { value: string; label: string }[] | null;
}

export interface ProductSpec {
  id: string;
  spec_key: string;
  spec_label: string;
  value_text: string | null;
  value_number: string | null;
  value_boolean: boolean | null;
  unit: string | null;
}

export interface ProductImage {
  id: string;
  file_url: string;
  thumbnail_url: string | null;
  alt_text: string | null;
  is_primary: boolean;
  sort_order: number;
}

export interface ProductListItem {
  id: string;
  product_code: string;
  name: string;
  slug: string;
  model: string | null;
  status: ProductStatus;
  pricing_mode: PricingMode;
  is_featured: boolean;
  is_demo: boolean;
  accepting_requests: boolean;
  updated_at: string;
  category: { id: string; name: string };
  brand: { id: string; name: string } | null;
  images: ProductImage[];
  _count: { prices: number; equipment_units: number };
}

export interface ProductDetail extends Omit<ProductListItem, '_count' | 'category' | 'brand'> {
  category_id: string;
  brand_id: string | null;
  short_description: string | null;
  description: string | null;
  show_public_price: boolean;
  seo_title: string | null;
  seo_description: string | null;
  published_at: string | null;
  category: { id: string; name: string; slug: string };
  brand: { id: string; name: string; slug: string } | null;
  specifications: ProductSpec[];
  service_areas: { location_id: string; location: { id: string; name: string } }[];
  spec_definitions: SpecDefinition[];
}

export interface RentalPrice {
  id: string;
  product_id: string;
  equipment_id: string | null;
  location_id: string | null;
  rental_period: RentalPeriod;
  price_basis: PriceBasis;
  price: number;
  min_period_count: number;
  valid_from: string | null;
  valid_to: string | null;
  is_public: boolean;
  status: 'active' | 'inactive';
  is_demo: boolean;
  location: { id: string; name: string } | null;
}

export interface SupplierPrice {
  id: string;
  supplier_id: string;
  product_id: string;
  location_id: string | null;
  rental_period: RentalPeriod;
  price_basis: PriceBasis;
  cost_price: number;
  valid_from: string | null;
  valid_to: string | null;
  status: 'active' | 'inactive';
  note: string | null;
  supplier: { id: string; name: string };
  location: { id: string; name: string } | null;
}

export interface PriceComparison {
  rental_period: RentalPeriod;
  location: { id: string | null; name: string };
  sell: {
    id: string;
    display: string;
    per_day: number;
    is_public: boolean;
    is_demo: boolean;
  } | null;
  best_cost?: {
    id: string;
    supplier: { id: string; name: string };
    display: string;
    per_day: number;
  } | null;
  gross_margin_percent?: number | null;
}

export interface PriceSheet {
  product: { id: string; name: string; product_code: string; pricing_mode: PricingMode };
  period_days: { days_per_week: number; days_per_month: number };
  rental_prices: RentalPrice[];
  supplier_prices?: SupplierPrice[];
  comparison: PriceComparison[];
}

export interface Supplier {
  id: string;
  supplier_code: string | null;
  name: string;
  contact_name: string | null;
  phone: string | null;
  email: string | null;
  address: string | null;
  tax_code: string | null;
  notes: string | null;
  status: 'active' | 'inactive';
  _count?: { equipment_units: number; supplier_prices: number };
}

export interface Setting {
  key: string;
  value: unknown;
  description: string | null;
  is_public: boolean;
  updated_at: string;
}

export interface AuditLog {
  id: string;
  action: string;
  entity_type: string;
  entity_id: string | null;
  before_data: unknown;
  after_data: unknown;
  ip_address: string | null;
  created_at: string;
  user: { id: string; full_name: string; email: string } | null;
}

export const PERIOD_LABEL: Record<RentalPeriod, string> = {
  day: 'Ngày',
  week: 'Tuần',
  month: 'Tháng',
};
export const STATUS_LABEL: Record<ProductStatus, string> = {
  draft: 'Nháp',
  published: 'Đang hiển thị',
  archived: 'Lưu trữ',
};
export const PRICING_MODE_LABEL: Record<PricingMode, string> = {
  fixed: 'Giá cố định',
  hybrid: 'Giá tham khảo + báo giá riêng',
  quote_only: 'Chỉ báo giá',
};

export function vnd(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return `${n.toLocaleString('vi-VN')}đ`;
}

// ───────── Sprint 3 ─────────

export const REQUEST_STATUS_LABEL: Record<RequestStatus, string> = {
  pending: 'Đã tiếp nhận',
  processing: 'Đang xử lý',
  waiting_customer: 'Chờ khách phản hồi',
  quoted: 'Đã gửi báo giá',
  accepted: 'Khách đã chấp nhận',
  rejected: 'Từ chối',
  converted: 'Đã chuyển đơn thuê',
  cancelled: 'Đã hủy',
};
export const REQUEST_STATUS_COLOR: Record<RequestStatus, string> = {
  pending: 'orange',
  processing: 'blue',
  waiting_customer: 'gold',
  quoted: 'cyan',
  accepted: 'green',
  rejected: 'red',
  converted: 'purple',
  cancelled: 'default',
};
export const SOURCE_LABEL: Record<string, string> = {
  zalo_miniapp: 'Zalo Mini App',
  website: 'Website',
  admin: 'Nhân viên nhập',
  other: 'Khác',
};

export interface RequestListItem {
  id: string;
  request_code: string;
  status: RequestStatus;
  status_label: string;
  contact_name: string;
  contact_phone: string;
  contact_company: string | null;
  delivery_address: string;
  rental_start_at: string;
  rental_end_at: string | null;
  source: string;
  created_at: string;
  item_count: number;
  items_summary: string;
  assigned_user: { id: string; full_name: string } | null;
  location: { id: string; name: string } | null;
}

export interface ProductSnapshot {
  name: string;
  product_code: string;
  model: string | null;
  brand: string | null;
  price_label: string;
  price: { amount: string; display: string; price_basis: string } | null;
  specs: { key: string; label: string; display: string }[];
}

export interface RequestDetail extends RequestListItem {
  customer_id: string;
  contact_email: string | null;
  contact_tax_code: string | null;
  delivery_at: string | null;
  pickup_at: string | null;
  customer_note: string | null;
  consent_at: string | null;
  consent_policy_version: string | null;
  allowed_transitions: RequestStatus[];
  customer: {
    id: string;
    full_name: string;
    company_name: string | null;
    phone: string;
    phone_verified: boolean;
    _count: { rental_requests: number };
  };
  items: {
    id: string;
    quantity: number;
    rental_period: 'day' | 'week' | 'month';
    expected_price: number | null;
    client_expected_price: number | null;
    customer_note: string | null;
    product_snapshot: ProductSnapshot;
    product: { id: string; slug: string; status: string };
  }[];
  status_history: {
    id: string;
    from_status: string | null;
    to_status: RequestStatus;
    note: string | null;
    created_at: string;
    user: { full_name: string } | null;
  }[];
  notes: { id: string; note: string; created_at: string; user: { full_name: string } }[];
  notifications: {
    id: string;
    status: 'pending' | 'sent' | 'failed' | 'skipped';
    error_code: string | null;
    error_message: string | null;
    sent_at: string | null;
    attempts: number;
    recipient: { id: string; full_name: string } | null;
  }[];
}

export interface RequestSummary {
  pending: number;
  unassigned: number;
  mine_open: number;
  failed_notifications_7d: number;
  latest_pending: {
    id: string;
    request_code: string;
    contact_name: string;
    created_at: string;
  } | null;
}

export interface Customer {
  id: string;
  customer_type: 'individual' | 'company';
  full_name: string;
  company_name: string | null;
  phone: string;
  phone_verified: boolean;
  email: string | null;
  tax_code: string | null;
  status: string;
  created_at: string;
  _count?: { rental_requests: number };
}

export interface CustomerDetail extends Customer {
  addresses: {
    id: string;
    label: string | null;
    address: string;
    is_default: boolean;
    location: { name: string } | null;
  }[];
  rental_requests: {
    id: string;
    request_code: string;
    status: RequestStatus;
    created_at: string;
    rental_start_at: string;
    assigned_user: { full_name: string } | null;
    _count: { items: number };
  }[];
}

export interface Equipment {
  id: string;
  product_id: string;
  equipment_code: string;
  ownership_type: 'owned' | 'partner';
  supplier_id?: string | null;
  brand_id: string | null;
  model: string | null;
  serial_number: string | null;
  manufacture_year: number | null;
  location_id: string | null;
  condition_status: string;
  operational_status: 'active' | 'maintenance' | 'inactive' | 'retired';
  internal_notes?: string | null;
  product: { id: string; name: string; product_code: string };
  brand: { id: string; name: string } | null;
  location: { id: string; name: string } | null;
  supplier?: { id: string; name: string; phone: string | null } | null;
}

export interface StaffUser {
  id: string;
  full_name: string;
  email: string;
  phone: string | null;
  status: 'active' | 'disabled';
  roles: string[];
  zalo_oa_user_id: string | null;
  notify_new_requests: boolean;
  last_login_at: string | null;
  locked_until: string | null;
}

export interface ZaloOaStatus {
  server_configured: boolean;
  oa_id: string | null;
  connected: boolean;
  access_expires_at: string | null;
  last_refreshed_at: string | null;
  last_error: string | null;
}

export const ROLE_LABEL: Record<string, string> = {
  super_admin: 'Quản trị cao nhất',
  admin: 'Quản trị',
  manager: 'Quản lý',
  sales: 'Kinh doanh',
  dispatcher: 'Điều phối',
  viewer: 'Chỉ xem',
};

export function phoneLocal(p: string | null | undefined): string {
  return p ? p.replace(/^\+84/, '0') : '';
}

// ───────────── Sprint 4: báo giá, đơn thuê, lịch xe ─────────────

export const QUOTE_STATUS_LABEL: Record<QuotationStatus, string> = {
  draft: 'Nháp',
  sent: 'Đã gửi khách',
  viewed: 'Khách đã xem',
  accepted: 'Khách chấp nhận',
  rejected: 'Khách từ chối',
  expired: 'Hết hạn',
  cancelled: 'Đã hủy',
};
export const QUOTE_STATUS_COLOR: Record<QuotationStatus, string> = {
  draft: 'default',
  sent: 'blue',
  viewed: 'geekblue',
  accepted: 'green',
  rejected: 'red',
  expired: 'orange',
  cancelled: 'default',
};
export const ITEM_TYPE_LABEL: Record<string, string> = {
  rental: 'Thuê thiết bị',
  transport: 'Vận chuyển',
  handling: 'Nâng hạ / lắp đặt',
  other: 'Chi phí khác',
};
export const BOOKING_STATUS_LABEL: Record<BookingStatus, string> = {
  confirmed: 'Đã xác nhận',
  preparing: 'Đang chuẩn bị xe',
  delivering: 'Đang giao xe',
  active: 'Đang cho thuê',
  returning: 'Đang thu hồi',
  completed: 'Hoàn thành',
  cancelled: 'Đã hủy',
};
export const BOOKING_STATUS_COLOR: Record<BookingStatus, string> = {
  confirmed: 'blue',
  preparing: 'cyan',
  delivering: 'geekblue',
  active: 'green',
  returning: 'purple',
  completed: 'default',
  cancelled: 'red',
};

export interface QuotationListItem {
  id: string;
  quote_code: string;
  version: number;
  status: QuotationStatus;
  status_label: string;
  total_amount: number;
  tax_rate: string | null;
  discount_amount: number;
  valid_until: string | null;
  sent_at: string | null;
  created_at: string;
  request: {
    id: string;
    request_code: string;
    contact_name: string;
    contact_company: string | null;
    assigned_user: { id: string; full_name: string } | null;
  };
  creator: { full_name: string };
  booking: { id: string; booking_code: string } | null;
}

export interface QuotationItem {
  id: string;
  item_type: 'rental' | 'transport' | 'handling' | 'other';
  request_item_id: string | null;
  product_id: string | null;
  equipment_id: string | null;
  product_name_snapshot: string;
  brand_snapshot: string | null;
  model_snapshot: string | null;
  quantity: number;
  rental_period: 'day' | 'week' | 'month' | null;
  period_count: string;
  unit_price: number;
  line_subtotal: number;
  tax_amount: number;
  line_total: number;
  notes: string | null;
  sort_order: number;
  cost_unit_price?: number | null;
  supplier_id?: string | null;
  supplier?: { id: string; name: string } | null;
  margin_percent?: number | null;
}

export interface QuotationDetail {
  id: string;
  quote_code: string;
  version: number;
  status: QuotationStatus;
  status_label: string;
  request_id: string;
  rental_subtotal: number;
  transport_fee: number;
  handling_fee: number;
  other_fee: number;
  subtotal: number;
  discount_amount: number;
  discount_percent: number;
  tax_rate: string | null;
  tax_amount: number;
  total_amount: number;
  valid_until: string | null;
  terms: string | null;
  customer_note: string | null;
  internal_note: string | null;
  sent_at: string | null;
  accepted_at: string | null;
  accepted_via: string | null;
  rejected_at: string | null;
  reject_reason: string | null;
  created_at: string;
  can_send: boolean;
  send_problems: string[];
  tax_options: { rate: number; label: string }[];
  items: QuotationItem[];
  cost_summary?: {
    lines_with_cost: number;
    revenue_with_cost: number;
    cost_total: number;
    margin_percent: number | null;
  };
  request: {
    id: string;
    request_code: string;
    status: string;
    contact_name: string;
    contact_phone: string;
    contact_company: string | null;
    delivery_address: string;
    delivery_at: string | null;
    rental_start_at: string;
    rental_end_at: string | null;
    pickup_at: string | null;
    location: { id: string; name: string } | null;
    assigned_user: { id: string; full_name: string } | null;
  };
  creator: { id: string; full_name: string };
  status_history: {
    id: string;
    from_status: string | null;
    to_status: QuotationStatus;
    note: string | null;
    created_at: string;
    user: { full_name: string } | null;
  }[];
  booking: { id: string; booking_code: string; status: BookingStatus } | null;
}

export interface BookingListItem {
  id: string;
  booking_code: string;
  status: BookingStatus;
  status_label: string;
  rental_start_at: string;
  rental_end_at: string | null;
  total_amount: number;
  created_at: string;
  unit_count: number;
  unassigned_count: number;
  customer: { full_name: string; company_name: string | null };
  quotation: {
    quote_code: string;
    request: { request_code: string; location: { name: string } | null };
  };
}

export interface BookingDetail {
  id: string;
  booking_code: string;
  status: BookingStatus;
  status_label: string;
  allowed_transitions: BookingStatus[];
  can_assign: boolean;
  delivery_at: string | null;
  rental_start_at: string;
  rental_end_at: string | null;
  pickup_at: string | null;
  total_amount: number;
  created_at: string;
  customer: { id: string; full_name: string; company_name: string | null; phone: string };
  quotation: {
    id: string;
    quote_code: string;
    request: {
      id: string;
      request_code: string;
      delivery_address: string;
      location: { name: string } | null;
      assigned_user: { id: string; full_name: string } | null;
    };
  };
  creator: { full_name: string };
  items: {
    id: string;
    product_id: string;
    quotation_item_id: string | null;
    unit_price: number;
    rental_period: string;
    period_count: string;
    product: { id: string; name: string; product_code: string };
    equipment: {
      id: string;
      equipment_code: string;
      model: string | null;
      ownership_type: string;
    } | null;
    supplier?: { id: string; name: string; phone: string | null } | null;
    from_partner?: boolean;
    cost_unit_price?: number | null;
    blocks: { id: string; start_at: string; end_at: string }[];
  }[];
  status_history: {
    id: string;
    from_status: string | null;
    to_status: BookingStatus;
    note: string | null;
    created_at: string;
    user: { full_name: string } | null;
  }[];
}

export interface AvailableEquipment {
  id: string;
  equipment_code: string;
  model: string | null;
  ownership_type: 'owned' | 'partner';
  location: { name: string } | null;
  brand: { name: string } | null;
  supplier?: { id: string; name: string } | null;
}

export interface ScheduleBlock {
  id: string;
  start_at: string;
  end_at: string;
  block_type: 'rental' | 'maintenance' | 'reserved' | 'manual';
  note: string | null;
  booking_item: {
    booking: {
      id: string;
      booking_code: string;
      status: BookingStatus;
      customer: { full_name: string; company_name: string | null };
    };
  } | null;
}

export interface ScheduleData {
  from: string;
  to: string;
  equipment: {
    id: string;
    equipment_code: string;
    model: string | null;
    ownership_type: 'owned' | 'partner';
    operational_status: string;
    product: { id: string; name: string };
    location: { name: string } | null;
    blocks: ScheduleBlock[];
  }[];
}

export interface DashboardSummary {
  products_published: number;
  equipment_units: number;
  requests_pending: number;
  requests_processing: number;
  quotations_draft: number;
  quotations_waiting_customer: number;
  bookings_in_progress: number;
}
